<template>
  <div class="pipelines-view">
    <div class="page-header">
      <div>
        <h1>Data Pipelines</h1>
        <p class="subtitle">Manage and execute your ETL workflows</p>
      </div>
      <router-link to="/ETL/pipelines/new" class="btn btn-primary">
        <Plus :size="18" />
        Create Pipeline
      </router-link>
    </div>

    <!-- Filters and Search -->
    <div v-if="!loading && !error && pipelines.length > 0" class="filters-section">
      <div class="search-box">
        <Search :size="18" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search pipelines..."
          class="search-input"
        />
      </div>
      <div class="view-toggle">
        <button
          @click="viewMode = 'table'"
          :class="['toggle-btn', { active: viewMode === 'table' }]"
          title="Table View"
        >
          <List :size="18" />
        </button>
        <button
          @click="viewMode = 'cards'"
          :class="['toggle-btn', { active: viewMode === 'cards' }]"
          title="Card View"
        >
          <LayoutGrid :size="18" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingSpinner v-if="loading" message="Loading pipelines..." />

    <!-- Error State -->
    <ErrorState
      v-else-if="error"
      title="Failed to Load Pipelines"
      :message="error"
    >
      <template #action>
        <button @click="fetchPipelines" class="btn btn-primary">
          Retry
        </button>
      </template>
    </ErrorState>

    <!-- Empty State -->
    <EmptyState
      v-else-if="pipelines.length === 0"
      title="No pipelines yet"
      message="Create your first pipeline to start extracting and transforming data."
    >
      <template #action>
        <router-link to="/ETL/pipelines/new" class="btn btn-primary">
          Create Your First Pipeline
        </router-link>
      </template>
    </EmptyState>

    <!-- Table View -->
    <div v-else-if="viewMode === 'table'" class="table-view">
      <div class="table-wrapper">
        <table class="pipelines-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Source</th>
              <th>Destination</th>
              <th>Last Run</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="pipeline in filteredPipelines"
              :key="pipeline.id"
              @click="openHistoryModal(pipeline)"
              class="pipeline-row"
            >
              <td class="pipeline-name">
                <strong>{{ pipeline.name }}</strong>
              </td>
              <td>
                <span :class="['status-badge', `status-${pipeline.status?.toLowerCase()}`]">
                  {{ pipeline.status || 'Draft' }}
                </span>
              </td>
              <td class="truncate">{{ pipeline.config?.extract?.endpoint || '-' }}</td>
              <td class="truncate">{{ pipeline.config?.load?.tableName || '-' }}</td>
              <td>{{ pipeline.lastRunAt ? formatDateTime(pipeline.lastRunAt) : 'Never' }}</td>
              <td class="actions-cell" @click.stop>
                <div class="action-buttons">
                  <button
                    @click.stop="openHistoryModal(pipeline)"
                    class="btn-icon"
                    title="View History"
                  >
                    <History :size="16" />
                  </button>
                  <router-link
                    :to="`/ETL/pipelines/${pipeline.id}/edit`"
                    class="btn-icon"
                    title="Edit"
                  >
                    <Edit :size="16" />
                  </router-link>
                  <button
                    @click.stop="handleExecutePipeline(pipeline)"
                    :disabled="!canExecutePipeline(pipeline)"
                    class="btn-icon btn-icon-success"
                    title="Execute"
                  >
                    <Play :size="16" />
                  </button>
                  <button
                    @click.stop="requestDeletePipeline(pipeline)"
                    class="btn-icon btn-icon-danger"
                    title="Delete"
                  >
                    <Trash2 :size="16" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Card View -->
    <div v-else class="pipeline-cards">
      <div
        v-for="pipeline in filteredPipelines"
        :key="pipeline.id"
        class="pipeline-card"
      >
        <div class="card-header">
          <div class="header-top">
            <div class="title-section">
              <h3>{{ pipeline.name }}</h3>
              <span :class="['status-badge', `status-${pipeline.status?.toLowerCase()}`]">
                {{ pipeline.status || 'Draft' }}
              </span>
            </div>
            <div class="card-actions">
              <router-link
                :to="`/ETL/pipelines/${pipeline.id}/edit`"
                class="btn btn-secondary btn-sm"
              >
                <Edit :size="16" />
                Edit
              </router-link>
              <button
                @click.stop="handleExecutePipeline(pipeline)"
                class="btn btn-success btn-sm"
                :disabled="!canExecutePipeline(pipeline)"
              >
                <Play :size="16" />
                Execute
              </button>
              <button
                @click.stop="requestDeletePipeline(pipeline)"
                class="btn btn-danger btn-sm"
              >
                <Trash2 :size="16" />
                Delete
              </button>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div class="pipeline-info-grid">
            <div v-if="pipeline.config?.extract?.endpoint" class="info-item highlight">
              <div class="info-label">
                <Database :size="14" />
                <strong>Source Endpoint</strong>
              </div>
              <span>{{ pipeline.config.extract.endpoint }}</span>
            </div>
            <div v-if="pipeline.config?.load?.tableName" class="info-item highlight">
              <div class="info-label">
                <Database :size="14" />
                <strong>Destination Table</strong>
              </div>
              <span>{{ pipeline.config.load.tableName }}</span>
            </div>
            <div class="info-item">
              <div class="info-label">
                <strong>Created</strong>
              </div>
              <span>{{ formatDateTime(pipeline.createdAt) }}</span>
            </div>
            <div v-if="pipeline.lastRunAt" class="info-item">
              <div class="info-label">
                <strong>Last Run</strong>
              </div>
              <span>{{ formatDateTime(pipeline.lastRunAt) }}</span>
            </div>
          </div>
        </div>

        <div class="card-footer" @click="toggleHistory(pipeline.id)">
          <div class="footer-content">
            <ChevronDown
              :size="18"
              :class="['chevron-icon', { expanded: expandedPipelineId === pipeline.id }]"
            />
            <span>Execution History</span>
            <span v-if="expandedPipelineId === pipeline.id && historyLastUpdated" class="last-updated">
              Updated {{ historyLastUpdated.toLocaleTimeString() }}
            </span>
          </div>
        </div>

        <!-- Expandable Execution History -->
        <div v-if="expandedPipelineId === pipeline.id" class="execution-history">
          <ExecutionHistoryList
            :executions="executions"
            :loading="loadingExecutions"
            :error="executionError"
            @refresh="refreshExecution"
          />
        </div>
      </div>
    </div>

    <!-- Execution History Modal -->
    <Teleport to="body">
      <div v-if="historyModalOpen" class="modal-overlay" @click="closeHistoryModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div>
              <h2>Execution History</h2>
              <p class="modal-subtitle">{{ selectedPipeline?.name }}</p>
            </div>
            <button @click="closeHistoryModal" class="modal-close">
              <X :size="20" />
            </button>
          </div>
          <div class="modal-body">
            <ExecutionHistoryList
              :executions="executions"
              :loading="loadingExecutions"
              :error="executionError"
              @refresh="refreshExecution"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <div v-if="pendingDeletePipeline" class="modal-overlay" @click="pendingDeletePipeline = null">
        <div class="modal-content modal-content-sm" @click.stop>
          <div class="modal-header">
            <div>
              <h2>Delete Pipeline</h2>
              <p class="modal-subtitle">This action cannot be undone.</p>
            </div>
            <button @click="pendingDeletePipeline = null" class="modal-close">
              <X :size="20" />
            </button>
          </div>
          <div class="modal-body">
            <p class="confirm-message">
              Are you sure you want to delete
              <strong>"{{ pendingDeletePipeline.name }}"</strong>?
            </p>
          </div>
          <div class="modal-footer">
            <button @click="pendingDeletePipeline = null" class="btn btn-secondary">
              Cancel
            </button>
            <button @click="confirmDeletePipeline" class="btn btn-danger">
              <Trash2 :size="16" />
              Delete Pipeline
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { Plus, Edit, Play, Trash2, ChevronDown, Database, Search, List, LayoutGrid, History, X } from 'lucide-vue-next'
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { usePipeline } from '@/Modules/ETL/composables/usePipeline'
import { withNotification, useNotification } from '@/Modules/ETL/composables/useNotification'
import LoadingSpinner from '@/Modules/ETL/components/common/LoadingSpinner.vue'
import EmptyState from '@/Modules/ETL/components/common/EmptyState.vue'
import ErrorState from '@/Modules/ETL/components/common/ErrorState.vue'
import ExecutionHistoryList from '@/Modules/ETL/components/common/ExecutionHistoryList.vue'
import apiService from '@/Modules/ETL/services/api'

const { loading, error, pipelines, fetchPipelines, executePipeline } = usePipeline()
const { error: notifyError, success: notifySuccess } = useNotification()

// View state
const viewMode = ref('table')
const searchQuery = ref('')

// Execution history state
const expandedPipelineId = ref(null)
const executions = ref([])
const loadingExecutions = ref(false)
const executionError = ref(null)
const historyModalOpen = ref(false)
const selectedPipeline = ref(null)

// Delete confirmation state
const pendingDeletePipeline = ref(null)

// Polling state for live execution status updates
const TERMINAL_STATUSES = new Set(['Completed', 'Failed', 'Cancelled'])
const POLL_INTERVAL_MS = 5000
const pollingInterval = ref(null)
const historyLastUpdated = ref(null)

const hasActiveExecutions = () =>
  executions.value.some((e) => !TERMINAL_STATUSES.has(e.status))

const stopPolling = () => {
  if (pollingInterval.value !== null) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

const startPolling = (pipelineId) => {
  stopPolling()
  pollingInterval.value = setInterval(async () => {
    await fetchExecutions(pipelineId)
    if (!hasActiveExecutions()) {
      stopPolling()
    }
  }, POLL_INTERVAL_MS)
}

// Filtered pipelines (applied to both table and card views)
const filteredPipelines = computed(() => {
  if (!searchQuery.value) return pipelines.value

  const query = searchQuery.value.toLowerCase()
  return pipelines.value.filter((pipeline) =>
    pipeline.name?.toLowerCase().includes(query) ||
    pipeline.config?.extract?.endpoint?.toLowerCase().includes(query) ||
    pipeline.config?.load?.tableName?.toLowerCase().includes(query)
  )
})

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString()
}

const canExecutePipeline = (pipeline) => {
  return pipeline.config?.extract?.endpoint != null
}

const openHistoryModal = async (pipeline) => {
  selectedPipeline.value = pipeline
  historyModalOpen.value = true
  await fetchExecutions(pipeline.id)
}

const closeHistoryModal = () => {
  historyModalOpen.value = false
  selectedPipeline.value = null
  executions.value = []
}

const toggleHistory = async (pipelineId) => {
  if (expandedPipelineId.value === pipelineId) {
    stopPolling()
    expandedPipelineId.value = null
    executions.value = []
    historyLastUpdated.value = null
  } else {
    expandedPipelineId.value = pipelineId
    await fetchExecutions(pipelineId)
    if (hasActiveExecutions()) {
      startPolling(pipelineId)
    }
  }
}

const fetchExecutions = async (pipelineId) => {
  loadingExecutions.value = true
  executionError.value = null
  try {
    const response = await apiService.getPipelineExecutions(pipelineId)
    executions.value = response.data
    historyLastUpdated.value = new Date()
  } catch (err) {
    const errorMessage = err && typeof err === 'object' && 'errorMessage' in err
      ? err.errorMessage
      : 'Failed to load execution history'
    executionError.value = errorMessage
    executions.value = []
  } finally {
    loadingExecutions.value = false
  }
}

const refreshExecution = async (executionId) => {
  try {
    const response = await apiService.getExecutionStatus(executionId)
    const updatedExecution = response.data
    const index = executions.value.findIndex((e) => e.id === executionId)
    if (index !== -1) {
      executions.value[index] = updatedExecution
    }
  } catch (err) {
    console.error('Failed to refresh execution:', err)
  }
}

const handleExecutePipeline = withNotification(
  async (pipeline) => {
    await executePipeline(pipeline.id)
    await fetchPipelines()

    if (expandedPipelineId.value === pipeline.id) {
      await fetchExecutions(pipeline.id)
      if (hasActiveExecutions()) {
        startPolling(pipeline.id)
      }
    }
  },
  {
    successTitle: 'Pipeline Executed',
    successMessage: 'Pipeline execution started successfully.',
    errorTitle: 'Execution Failed',
    fallbackErrorMessage: 'Failed to execute pipeline.'
  }
)

const requestDeletePipeline = (pipeline) => {
  pendingDeletePipeline.value = pipeline
}

const confirmDeletePipeline = async () => {
  const pipeline = pendingDeletePipeline.value
  if (!pipeline) return
  pendingDeletePipeline.value = null

  try {
    await apiService.removePipeline(pipeline.id)

    if (expandedPipelineId.value === pipeline.id) {
      expandedPipelineId.value = null
      executions.value = []
    }

    notifySuccess('Pipeline Deleted', `"${pipeline.name}" has been removed.`)
    await fetchPipelines()
  } catch (err) {
    const errorMessage = err && typeof err === 'object' && 'errorMessage' in err
      ? err.errorMessage
      : err?.message || 'Unknown error'
    notifyError('Delete Failed', errorMessage)
  }
}

onMounted(() => {
  fetchPipelines()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.pipelines-view {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0 0 0.5rem 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.subtitle {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 15px;
}

/* Filters Section */
.filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.search-box {
  flex: 1;
  max-width: 400px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
}

.search-box svg {
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  font-size: 14px;
  color: var(--color-text-primary);
}

.search-input::placeholder {
  color: var(--color-text-muted);
}

.view-toggle {
  display: flex;
  gap: 0.5rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 8px;
}

.toggle-btn {
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover {
  background: white;
  color: var(--color-accent);
}

.toggle-btn.active {
  background: white;
  color: var(--color-primary);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

/* Table View */
.table-view {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.pipelines-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.pipelines-table thead {
  background: #f8fafc;
}

.pipelines-table th {
  padding: 1rem 1.25rem;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.pipelines-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: pointer;
}

.pipelines-table tbody tr:hover {
  background: #f8fafc;
}

.pipelines-table td {
  padding: 1rem 1.25rem;
  color: var(--color-text-primary);
}

.pipeline-name strong {
  color: var(--color-text-primary);
  font-weight: 600;
}

.truncate {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.actions-cell {
  width: 180px;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Card View */
.pipeline-cards {
  display: grid;
  gap: 1.5rem;
}

.pipeline-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}

.pipeline-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.card-header h3 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 20px;
  font-weight: 600;
}

.card-body {
  padding: 1.5rem;
}

.pipeline-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.info-item.highlight {
  background: rgba(102, 126, 234, 0.05);
  border-color: rgba(102, 126, 234, 0.2);
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-item strong {
  color: var(--color-text-primary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  color: var(--color-text-secondary);
  font-size: 14px;
  word-break: break-word;
  line-height: 1.4;
}

.card-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.card-footer {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.card-footer:hover {
  background: #f1f5f9;
}

.footer-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  font-weight: 500;
  font-size: 14px;
}

.last-updated {
  margin-left: auto;
  font-size: 12px;
  color: var(--color-text-muted);
}

.chevron-icon {
  transition: transform 0.3s ease;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.chevron-icon.expanded {
  transform: rotate(180deg);
}

/* Icon Buttons */
.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: #f1f5f9;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-icon:hover:not(:disabled) {
  background: var(--color-accent);
  color: white;
  transform: scale(1.05);
}

.btn-icon-success {
  background: var(--color-success-light);
  color: var(--color-success);
}

.btn-icon-success:hover:not(:disabled) {
  background: var(--color-success);
  color: white;
}

.btn-icon-danger {
  background: var(--color-error-light);
  color: var(--color-error);
}

.btn-icon-danger:hover:not(:disabled) {
  background: var(--color-error);
  color: white;
}

.btn-icon:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

/* Expandable Execution History */
.execution-history {
  padding: 1.5rem;
  background: #f1f5f9;
  border-top: 2px solid #e2e8f0;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; max-height: 0; }
  to { opacity: 1; max-height: 2000px; }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  animation: fadeIn 0.2s ease;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

.modal-content-sm {
  max-width: 480px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0 0 0.25rem 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.modal-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
}

.modal-close {
  background: transparent;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: #f1f5f9;
  color: var(--color-text-primary);
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
}

.confirm-message {
  font-size: 15px;
  color: var(--color-text-primary);
  line-height: 1.6;
}
</style>
